import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, DollarSign, BarChart3, Calendar, Download, RefreshCw, ShoppingCart, Package } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import apiService from '../services/apiService';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [compareMode, setCompareMode] = useState(false);

  // Date range
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [periodType, setPeriodType] = useState('monthly'); // daily, weekly, monthly

  // Report data
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);

  // Processed chart data
  const [salesTrend, setSalesTrend] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [paymentMethodBreakdown, setPaymentMethodBreakdown] = useState([]);
  const [revenueVsExpenses, setRevenueVsExpenses] = useState([]);
  const [companyComparison, setCompanyComparison] = useState([]);
  const [dailySales, setDailySales] = useState([]);
  const [customerRanking, setCustomerRanking] = useState([]);

  // Summary
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    totalInvoices: 0,
    totalExpenses: 0,
    averageInvoice: 0,
    paidInvoices: 0,
    unpaidInvoices: 0
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadReportData();
    }
  }, [selectedCompanyId, dateRange]);

  useEffect(() => {
    if (invoices.length > 0 || expenses.length > 0) {
      processData();
    }
  }, [invoices, expenses, periodType]);

  useEffect(() => {
    if (compareMode && companies.length > 1) {
      loadCompanyComparison();
    }
  }, [compareMode, companies, dateRange]);

  const loadCompanies = async () => {
    try {
      const data = await apiService.getAllCompanies();
      setCompanies(data);
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      if (savedCompanyId && data.find(c => c.id === parseInt(savedCompanyId))) {
        setSelectedCompanyId(savedCompanyId);
        setSelectedCompanyName(data.find(c => c.id === parseInt(savedCompanyId))?.name || '');
      } else if (data.length > 0) {
        setSelectedCompanyId(data[0].id.toString());
        setSelectedCompanyName(data[0].name);
        localStorage.setItem('selectedCompanyId', data[0].id.toString());
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
    const company = companies.find(c => c.id === parseInt(companyId));
    setSelectedCompanyName(company?.name || '');
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      apiService.refreshTokenFromStorage();

      const [invoiceData, productData, expenseData, dashboard] = await Promise.all([
        selectedCompanyId ? apiService.getInvoicesByCompany(selectedCompanyId) : apiService.getAllInvoices(),
        selectedCompanyId ? apiService.getProductsByCompany(selectedCompanyId) : apiService.getAllProducts(),
        selectedCompanyId ? apiService.getExpensesByCompany(selectedCompanyId) : apiService.getAllExpenses(),
        selectedCompanyId ? apiService.getDashboardDataByCompany(selectedCompanyId) : null
      ]);

      // Filter by date range
      const filteredInvoices = invoiceData.filter(inv => {
        const invDate = new Date(inv.invoiceDate);
        return invDate >= new Date(dateRange.startDate) && invDate <= new Date(dateRange.endDate);
      });

      const filteredExpenses = expenseData.filter(exp => {
        const expDate = new Date(exp.expenseDate || exp.createdAt);
        return expDate >= new Date(dateRange.startDate) && expDate <= new Date(dateRange.endDate);
      });

      setInvoices(filteredInvoices);
      setProducts(productData);
      setExpenses(filteredExpenses);
      setDashboardData(dashboard);
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyComparison = async () => {
    try {
      const comparisonData = [];
      for (const company of companies) {
        const companyInvoices = await apiService.getInvoicesByCompany(company.id);
        const filtered = companyInvoices.filter(inv => {
          const invDate = new Date(inv.invoiceDate);
          return invDate >= new Date(dateRange.startDate) && invDate <= new Date(dateRange.endDate);
        });

        const revenue = filtered.reduce((sum, inv) => sum + parseFloat(inv.totalAmount || 0), 0);
        const paid = filtered.reduce((sum, inv) => sum + parseFloat(inv.paidAmount || 0), 0);
        const outstanding = filtered.reduce((sum, inv) => sum + parseFloat(inv.balanceAmount || 0), 0);

        comparisonData.push({
          name: company.name,
          revenue,
          paid,
          outstanding,
          invoiceCount: filtered.length
        });
      }
      setCompanyComparison(comparisonData);
    } catch (error) {
      console.error('Error loading company comparison:', error);
    }
  };

  const processData = () => {
    // Summary
    const totalRevenue = invoices.reduce((sum, inv) => sum + parseFloat(inv.totalAmount || 0), 0);
    const totalPaid = invoices.reduce((sum, inv) => sum + parseFloat(inv.paidAmount || 0), 0);
    const totalOutstanding = invoices.reduce((sum, inv) => sum + parseFloat(inv.balanceAmount || 0), 0);
    const totalExpensesAmount = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || exp.totalPrice || 0), 0);

    setSummary({
      totalRevenue,
      totalPaid,
      totalOutstanding,
      totalInvoices: invoices.length,
      totalExpenses: totalExpensesAmount,
      averageInvoice: invoices.length > 0 ? totalRevenue / invoices.length : 0,
      paidInvoices: invoices.filter(i => i.status === 'PAID').length,
      unpaidInvoices: invoices.filter(i => i.status === 'UNPAID' || i.status === 'PARTIALLY_PAID').length
    });

    // Sales trend by period
    processSalesTrend();

    // Invoice status distribution
    const statusCounts = {};
    invoices.forEach(inv => {
      statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
    });
    setStatusDistribution(Object.entries(statusCounts).map(([name, value]) => ({ name, value })));

    // Top products by revenue
    const productRevenue = {};
    invoices.forEach(inv => {
      (inv.items || []).forEach(item => {
        const productName = item.product?.name || item.description || 'Unknown';
        const lineTotal = parseFloat(item.lineTotal || (item.unitPrice * item.quantity) || 0);
        productRevenue[productName] = (productRevenue[productName] || 0) + lineTotal;
      });
    });
    const sortedProducts = Object.entries(productRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, revenue]) => ({ name, revenue }));
    setTopProducts(sortedProducts);

    // Payment method breakdown
    const methodCounts = {};
    invoices.forEach(inv => {
      (inv.payments || []).forEach(payment => {
        const method = payment.paymentMethod || 'Unknown';
        methodCounts[method] = (methodCounts[method] || 0) + parseFloat(payment.amount || 0);
      });
    });
    setPaymentMethodBreakdown(Object.entries(methodCounts).map(([name, value]) => ({ name, value })));

    // Revenue vs Expenses by month
    processRevenueVsExpenses();

    // Top customers
    const customerRevenue = {};
    invoices.forEach(inv => {
      const customerName = inv.customer?.name || 'Unknown';
      customerRevenue[customerName] = (customerRevenue[customerName] || 0) + parseFloat(inv.totalAmount || 0);
    });
    const sortedCustomers = Object.entries(customerRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, revenue]) => ({ name, revenue }));
    setCustomerRanking(sortedCustomers);
  };

  const processSalesTrend = () => {
    const groupedData = {};

    invoices.forEach(inv => {
      const date = new Date(inv.invoiceDate);
      let key;

      if (periodType === 'daily') {
        key = date.toISOString().split('T')[0];
      } else if (periodType === 'weekly') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }

      if (!groupedData[key]) {
        groupedData[key] = { period: key, revenue: 0, paid: 0, outstanding: 0, count: 0 };
      }
      groupedData[key].revenue += parseFloat(inv.totalAmount || 0);
      groupedData[key].paid += parseFloat(inv.paidAmount || 0);
      groupedData[key].outstanding += parseFloat(inv.balanceAmount || 0);
      groupedData[key].count += 1;
    });

    const sorted = Object.values(groupedData).sort((a, b) => a.period.localeCompare(b.period));

    // Format period labels
    sorted.forEach(item => {
      if (periodType === 'daily') {
        item.label = new Date(item.period).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      } else if (periodType === 'weekly') {
        item.label = `W/c ${new Date(item.period).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
      } else {
        const [year, month] = item.period.split('-');
        item.label = new Date(year, month - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
      }
    });

    setSalesTrend(sorted);
  };

  const processRevenueVsExpenses = () => {
    const monthlyData = {};

    invoices.forEach(inv => {
      const date = new Date(inv.invoiceDate);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[key]) {
        monthlyData[key] = { period: key, revenue: 0, expenses: 0, profit: 0 };
      }
      monthlyData[key].revenue += parseFloat(inv.totalAmount || 0);
    });

    expenses.forEach(exp => {
      const date = new Date(exp.expenseDate || exp.createdAt);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[key]) {
        monthlyData[key] = { period: key, revenue: 0, expenses: 0, profit: 0 };
      }
      monthlyData[key].expenses += parseFloat(exp.amount || exp.totalPrice || 0);
    });

    const sorted = Object.values(monthlyData)
      .sort((a, b) => a.period.localeCompare(b.period))
      .map(item => {
        const [year, month] = item.period.split('-');
        return {
          ...item,
          profit: item.revenue - item.expenses,
          label: new Date(year, month - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        };
      });

    setRevenueVsExpenses(sorted);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000) return `KES ${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `KES ${(amount / 1000).toFixed(1)}K`;
    return `KES ${amount.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CountTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleExportCSV = () => {
    const headers = ['Period', 'Revenue', 'Paid', 'Outstanding', 'Invoice Count'];
    const rows = salesTrend.map(item => [
      item.label, item.revenue.toFixed(2), item.paid.toFixed(2), item.outstanding.toFixed(2), item.count
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${selectedCompanyName}_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setQuickRange = (range) => {
    const now = new Date();
    let start;
    switch (range) {
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        now.setDate(0);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'last6Months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        now.setFullYear(now.getFullYear() - 1, 11, 31);
        break;
      default:
        return;
    }
    setDateRange({
      startDate: start.toISOString().split('T')[0],
      endDate: range === 'lastMonth' || range === 'lastYear' ? now.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Reports & Analytics</h1>
          <p className="text-gray-600">Track sales trends, revenue, and performance across companies</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <button
            onClick={handleExportCSV}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
          <button
            onClick={loadReportData}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Company Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
            <div className="flex items-center space-x-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <select
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Period Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Group By</label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Compare Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compare</label>
            <button
              onClick={() => setCompareMode(!compareMode)}
              className={`w-full px-3 py-2 border rounded-lg transition-colors ${
                compareMode
                  ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {compareMode ? 'Comparing All' : 'Compare Companies'}
            </button>
          </div>

          {/* Quick Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quick Range</label>
            <select
              onChange={(e) => { if (e.target.value) setQuickRange(e.target.value); e.target.value = ''; }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              defaultValue=""
            >
              <option value="">Select...</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="thisQuarter">This Quarter</option>
              <option value="last6Months">Last 6 Months</option>
              <option value="thisYear">This Year</option>
              <option value="lastYear">Last Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(summary.totalRevenue)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{summary.totalInvoices} invoices</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Collected</p>
              <p className="text-xl font-bold text-green-600 mt-1">{formatCurrency(summary.totalPaid)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{summary.paidInvoices} paid invoices</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Outstanding</p>
              <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(summary.totalOutstanding)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <BarChart3 className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{summary.unpaidInvoices} unpaid</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Net Profit</p>
              <p className={`text-xl font-bold mt-1 ${summary.totalRevenue - summary.totalExpenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.totalRevenue - summary.totalExpenses)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Expenses: {formatCurrency(summary.totalExpenses)}</p>
        </div>
      </div>

      {/* Company Comparison Chart */}
      {compareMode && companyComparison.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Company Comparison</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={companyComparison} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#4f46e5" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Collected" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outstanding" name="Outstanding" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sales Trend Chart */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Sales Trend - {selectedCompanyName}
          </h3>
          <span className="text-sm text-gray-500">{periodType.charAt(0).toUpperCase() + periodType.slice(1)} view</span>
        </div>
        {salesTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={salesTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="paidGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#4f46e5" fill="url(#revenueGradient)" strokeWidth={2} />
              <Area type="monotone" dataKey="paid" name="Collected" stroke="#10b981" fill="url(#paidGradient)" strokeWidth={2} />
              <Line type="monotone" dataKey="outstanding" name="Outstanding" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-gray-500">No sales data for the selected period</div>
        )}
      </div>

      {/* Invoice Count Trend */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Volume</h3>
        {salesTrend.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={salesTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CountTooltip />} />
              <Bar dataKey="count" name="Invoices" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">No data available</div>
        )}
      </div>

      {/* Revenue vs Expenses */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue vs Expenses (Monthly)</h3>
        {revenueVsExpenses.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={revenueVsExpenses} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#4f46e5" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="profit" name="Profit" stroke="#10b981" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">No data available</div>
        )}
      </div>

      {/* Charts Row - Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invoice Status Distribution */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Status Distribution</h3>
          {statusDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">No data available</div>
          )}
        </div>

        {/* Payment Method Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Method Breakdown</h3>
          {paymentMethodBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={paymentMethodBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentMethodBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">No payment data available</div>
          )}
        </div>
      </div>

      {/* Top Products & Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products by Revenue */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top 10 Products by Revenue</h3>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={topProducts} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" fill="#4f46e5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">No product data available</div>
          )}
        </div>

        {/* Top Customers by Revenue */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top 10 Customers by Revenue</h3>
          {customerRanking.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={customerRanking} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">No customer data available</div>
          )}
        </div>
      </div>

      {/* Detailed Data Table */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Period Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Collected</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Collection %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salesTrend.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.label}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.count}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(item.revenue)}</td>
                  <td className="px-4 py-3 text-sm text-green-600 text-right">{formatCurrency(item.paid)}</td>
                  <td className="px-4 py-3 text-sm text-red-600 text-right">{formatCurrency(item.outstanding)}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.revenue > 0
                        ? ((item.paid / item.revenue) * 100) >= 80
                          ? 'bg-green-100 text-green-800'
                          : ((item.paid / item.revenue) * 100) >= 50
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.revenue > 0 ? `${((item.paid / item.revenue) * 100).toFixed(1)}%` : '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            {salesTrend.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr className="font-medium">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{summary.totalInvoices}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(summary.totalRevenue)}</td>
                  <td className="px-4 py-3 text-sm text-green-600 text-right">{formatCurrency(summary.totalPaid)}</td>
                  <td className="px-4 py-3 text-sm text-red-600 text-right">{formatCurrency(summary.totalOutstanding)}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {summary.totalRevenue > 0 ? `${((summary.totalPaid / summary.totalRevenue) * 100).toFixed(1)}%` : '-'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;